import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  type CountryCode,
  type EntityType,
  type KycStatus,
  type Sector,
} from '@shared';
import { apiGet, ApiError } from '../../lib/api';
import { useAuth } from '../../lib/auth-context';

// Client-side affordance gate. The server is the authority; this just hides
// the CTA from roles that would be 403'd at the API anyway (REQ-004).
const CLIENT_WRITE_ROLES = new Set(['super', 'admin', 'lead']);

interface ClientRow {
  id: string;
  name: string;
  entity_type: EntityType;
  registration_number: string;
  country: CountryCode;
  sector: Sector;
  kyc: KycStatus;
  kyc_depth?: 'simplified' | 'standard' | 'enhanced';
  sanctions_status?: 'pending' | 'clear' | 'flagged';
  status?: 'prospect' | 'active' | 'suspended' | 'deactivated';
  open_requests?: number;
  created_at?: string;
}

const KYC_TONE: Record<KycStatus, string> = {
  current: 'green',
  refresh: 'amber',
  pending: 'amber',
  in_review: 'accent',
  expired: 'red',
  rejected: 'red',
};

const KYC_LABEL: Record<KycStatus, string> = {
  current: 'Current',
  refresh: 'Refresh due',
  pending: 'Pending',
  in_review: 'In review',
  expired: 'Expired',
  rejected: 'Rejected',
};

type Filter = 'all' | KycStatus;

export function AdminClients() {
  const { user } = useAuth();
  const nav = useNavigate();
  const canCreateClient = CLIENT_WRITE_ROLES.has(user?.role ?? '');

  const [rows, setRows] = useState<ClientRow[] | null>(null);
  const [filter, setFilter] = useState<Filter>('all');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    apiGet<{ clients: ClientRow[] }>('/api/clients')
      .then((r) => setRows(r.clients))
      .catch((err: Error) => setError(err instanceof ApiError ? err.serverMessage : err.message ?? 'Failed to load clients.'));
  }, []);

  const counts = useMemo(() => {
    const c: Record<KycStatus, number> = { current: 0, refresh: 0, pending: 0, in_review: 0, expired: 0, rejected: 0 };
    for (const r of rows ?? []) c[r.kyc] = (c[r.kyc] ?? 0) + 1;
    return c;
  }, [rows]);

  // Sign-up applications awaiting compliance review. Heuristic: kyc=pending
  // AND status is prospect or unset (the state newly-submitted /portal/sign-up
  // rows are born in). Sorted most-recent-first so newest applications are top.
  const pendingApplications = useMemo(
    () => (rows ?? [])
      .filter((r) => r.kyc === 'pending' && (r.status === 'prospect' || r.status === undefined))
      .sort((a, b) => (b.created_at ?? '').localeCompare(a.created_at ?? '')),
    [rows],
  );

  const list = useMemo(
    () => (rows ?? []).filter((r) => (filter === 'all' ? true : r.kyc === filter)),
    [rows, filter],
  );

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 16 }}>
        <div>
          <h1 className="portal-h1">Clients</h1>
          <p className="portal-sub">
            Body-corporate clients on file. KYC and sanctions status are the gating signals for new
            engagements; clients with <strong>refresh</strong> or <strong>expired</strong> require Compliance review before a new proposal is sent.
          </p>
        </div>
        {canCreateClient && (
          <Link to="/admin/clients/new" className="btn accent">+ New client</Link>
        )}
      </div>

      <div className="portal-cards">
        <div className="card">
          <div className="lbl">Total</div>
          <div className="val">{rows?.length ?? '—'}</div>
          <div className="delta">On file</div>
        </div>
        <div className="card">
          <div className="lbl">KYC current</div>
          <div className="val">{counts.current}</div>
          <div className="delta">Ready to engage</div>
        </div>
        <div className="card">
          <div className="lbl">Refresh / pending</div>
          <div className="val">{counts.refresh + counts.pending}</div>
          <div className="delta">Compliance attention</div>
        </div>
        <div className="card">
          <div className="lbl">Blocked</div>
          <div className="val">{counts.expired + counts.rejected}</div>
          <div className="delta">Expired / rejected</div>
        </div>
      </div>

      {pendingApplications.length > 0 && (
        <div style={{
          marginTop: 28,
          padding: 20,
          border: '1px solid var(--accent)',
          borderRadius: 'var(--r-lg)',
          background: 'color-mix(in oklab, var(--accent) 6%, transparent)',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, marginBottom: 14 }}>
            <div>
              <h3 style={{ margin: 0, fontSize: 16, letterSpacing: '-0.005em' }}>
                <span style={{ color: 'var(--accent)' }}>●</span>{' '}
                {pendingApplications.length} pending application{pendingApplications.length === 1 ? '' : 's'} awaiting compliance review
              </h3>
              <p style={{ margin: '4px 0 0', fontSize: 12.5, color: 'var(--fg-soft)', lineHeight: 1.55 }}>
                New sign-ups submitted via <span style={{ fontFamily: 'var(--mono)' }}>/portal/sign-up</span>. Open each one to verify the company, run sanctions screening, and activate the contact.
              </p>
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {pendingApplications.map((c) => (
              <div
                key={c.id}
                onClick={() => nav(`/admin/clients/${c.id}`)}
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    nav(`/admin/clients/${c.id}`);
                  }
                }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 16,
                  padding: '12px 14px',
                  background: 'var(--bg)', borderRadius: 'var(--r)',
                  border: '1px solid var(--rule)',
                  cursor: 'pointer',
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 500, fontSize: 14 }}>{c.name}</div>
                  <div style={{ fontSize: 12, color: 'var(--fg-mute)', fontFamily: 'var(--mono)', marginTop: 2 }}>
                    {c.id} · {c.entity_type} · {c.country} · {c.sector}
                  </div>
                </div>
                {c.created_at && (
                  <div style={{ fontSize: 11, color: 'var(--fg-mute)', whiteSpace: 'nowrap' }}>
                    {new Date(c.created_at).toLocaleString('en-GB', {
                      day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
                    })}
                  </div>
                )}
                <span style={{ fontSize: 13, color: 'var(--accent)', fontWeight: 500 }}>Review →</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="tabs" style={{ marginTop: 24 }}>
        {(['all', 'current', 'refresh', 'pending', 'expired', 'rejected'] as Filter[]).map((t) => (
          <button key={t} className={filter === t ? 'on' : ''} onClick={() => setFilter(t)}>
            {t === 'all' ? 'All' : KYC_LABEL[t]}
          </button>
        ))}
      </div>

      <div className="card flush">
        <table className="tbl">
          <thead>
            <tr>
              <th>Client</th>
              <th>Type</th>
              <th>Country</th>
              <th>Sector</th>
              <th>KYC</th>
              <th>Sanctions</th>
              <th className="amt">Open briefs</th>
            </tr>
          </thead>
          <tbody>
            {rows === null && (
              <tr><td colSpan={7} style={{ color: 'var(--fg-mute)', fontSize: 13 }}>Loading…</td></tr>
            )}
            {error && (
              <tr><td colSpan={7} style={{ color: 'var(--red)', fontSize: 13 }}>{error}</td></tr>
            )}
            {rows !== null && !error && list.length === 0 && (
              <tr>
                <td colSpan={7} style={{ color: 'var(--fg-mute)', fontSize: 13, textAlign: 'center', padding: 32 }}>
                  No clients match this filter.
                </td>
              </tr>
            )}
            {list.map((c) => (
              <tr
                key={c.id}
                className="row-clickable"
                onClick={() => nav(`/admin/clients/${c.id}`)}
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    nav(`/admin/clients/${c.id}`);
                  }
                }}
              >
                <td>
                  <div style={{ fontWeight: 500 }}>{c.name}</div>
                  <div style={{ fontSize: 12, color: 'var(--fg-mute)', fontFamily: 'var(--mono)' }}>
                    {c.id} · {c.registration_number}
                  </div>
                </td>
                <td style={{ color: 'var(--fg-soft)', fontSize: 13 }}>{c.entity_type}</td>
                <td style={{ color: 'var(--fg-soft)', fontSize: 13 }}>{c.country}</td>
                <td style={{ color: 'var(--fg-soft)', fontSize: 13 }}>{c.sector}</td>
                <td>
                  <span className={`chip ${KYC_TONE[c.kyc]}`}>
                    <span style={{ width: 5, height: 5, borderRadius: '50%', background: 'currentColor' }} />
                    {KYC_LABEL[c.kyc]}
                  </span>
                </td>
                <td style={{ fontSize: 13 }}>
                  {c.sanctions_status === 'flagged' ? (
                    <span style={{ color: 'var(--red)' }}>Flagged</span>
                  ) : c.sanctions_status === 'clear' ? (
                    <span style={{ color: 'var(--fg-soft)' }}>Clear</span>
                  ) : (
                    <span style={{ color: 'var(--fg-mute)' }}>Pending</span>
                  )}
                </td>
                <td className="amt" style={{ fontVariantNumeric: 'tabular-nums' }}>
                  {c.open_requests ?? 0}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}
